import { computed, inject } from '@angular/core';
import {
	patchState,
	signalStore,
	withComputed,
	withMethods,
	withState,
} from '@ngrx/signals';

import { LLMStore } from '@jepto/features/datachat/stores/llm.store';

import type {
	AiCreditsIncentiveId,
	AiCreditsIncentiveItem,
	AiCreditsIncentiveRejectionCode,
} from '../models/ai-credits-incentive.model';
import { createDefaultAiCreditsIncentiveItems } from '../constants/ai-credits-incentives.config';
import { AiCreditsIncentiveService } from '../services/ai-credits-incentive.service';

export type AiCreditsIncentiveCallState =
	| 'idle'
	| 'loading'
	| 'success'
	| 'error';

type AiCreditsIncentiveStoreState = {
	incentives: AiCreditsIncentiveItem[];
	hideIncentive: boolean;
	loadState: AiCreditsIncentiveCallState;
	submitState: AiCreditsIncentiveCallState;
	submitError: string | null;
	/**
	 * The code behind the current `submitError`, when the backend rejected the
	 * submission. Kept alongside the message so callers can act on a specific
	 * rejection (e.g. offering a link to account settings for
	 * MISSING_ACCOUNT_DOMAIN) rather than matching on the wording.
	 */
	submitRejectionCode: AiCreditsIncentiveRejectionCode | null;
};

const initialState: AiCreditsIncentiveStoreState = {
	incentives: createDefaultAiCreditsIncentiveItems(),
	hideIncentive: false,
	loadState: 'idle',
	submitState: 'idle',
	submitError: null,
	submitRejectionCode: null,
};

export const AiCreditsIncentiveStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withComputed(store => ({
		isLoading: computed(() => store.loadState() === 'loading'),
		hasIncentives: computed(() => store.incentives().length > 0),
		/** Dismissed, or both offers already verified so there is nothing left to claim. */
		isHidden: computed(() => {
			if (store.hideIncentive()) {
				return true;
			}
			const incentives = store.incentives();
			return (
				incentives.length > 0 &&
				incentives.every(item => item.status === 'verified')
			);
		}),
	})),
	withMethods(
		(
			store,
			incentiveService = inject(AiCreditsIncentiveService),
			llmStore = inject(LLMStore)
		) => {
			/**
			 * Bumped when a submit starts or is abandoned (clearSubmitError).
			 * Used so abandoned requests do not leak submitState/submitError onto
			 * the next modal.
			 */
			let submitGeneration = 0;
			/**
			 * Latest submit generation per incentive. A slower abandoned response
			 * must not overwrite a newer claim for the same id.
			 */
			const latestSubmitByIncentive = new Map<
				AiCreditsIncentiveId,
				number
			>();

			return {
				async loadIncentives(): Promise<void> {
					patchState(store, { loadState: 'loading', submitError: null });
					try {
						const incentives = await incentiveService.listIncentives();
						patchState(store, {
							incentives,
							hideIncentive: incentiveService.isHideIncentive(),
							loadState: 'success',
						});
					} catch {
						patchState(store, { loadState: 'error' });
					}
				},

				async submitIncentive(
					incentiveId: AiCreditsIncentiveId,
					proofUrl: string
				): Promise<boolean> {
					const generation = ++submitGeneration;
					latestSubmitByIncentive.set(incentiveId, generation);
					patchState(store, {
						submitState: 'loading',
						submitError: null,
						submitRejectionCode: null,
					});

					try {
						const result = await incentiveService.submitIncentive({
							incentiveId,
							proofUrl,
						});
						// Superseded by a newer submit for the same incentive — drop.
						if (
							latestSubmitByIncentive.get(incentiveId) !==
							generation
						) {
							return false;
						}
						const abandoned = generation !== submitGeneration;
						const incentives = store.incentives().map(item =>
							item.id === result.id
								? {
										...item,
										status: result.status,
										proofUrl: result.proofUrl ?? proofUrl,
										lastVerifiedAt:
											result.lastVerifiedAt ?? null,
										statusMessage:
											result.statusMessage ?? null,
										rejectionCode:
											result.rejectionCode ?? null,
										rejectionReason:
											result.rejectionReason ?? null,
									}
								: item
						);

						// Persist when this is still the latest submit for the id.
						// Skip submitState/submitError when the modal UI was abandoned.
						if (result.status === 'rejected') {
							if (abandoned) {
								patchState(store, { incentives });
							} else {
								patchState(store, {
									incentives,
									submitState: 'error',
									submitError:
										result.rejectionReason ??
										result.statusMessage ??
										'We could not verify this submission. Please check it and try again.',
									submitRejectionCode:
										result.rejectionCode ?? null,
								});
							}
							return false;
						}

						if (abandoned) {
							patchState(store, { incentives });
						} else {
							patchState(store, {
								incentives,
								submitState: 'success',
							});
						}

						if (result.status === 'verified') {
							llmStore.getAccountAICredits();
						}
						// Abandoned: caller must not toast/close — UI already moved on.
						return !abandoned;
					} catch (err: unknown) {
						if (
							latestSubmitByIncentive.get(incentiveId) !==
							generation
						) {
							return false;
						}
						if (generation !== submitGeneration) {
							return false;
						}
						const message =
							err instanceof Error
								? err.message
								: 'Could not submit incentive. Please try again.';
						patchState(store, {
							submitState: 'error',
							submitError: message,
							submitRejectionCode: null,
						});
						return false;
					}
				},

				clearSubmitError(): void {
					submitGeneration++;
					patchState(store, {
						submitError: null,
						submitRejectionCode: null,
						submitState: 'idle',
					});
				},

				async dismissIncentivesPanel(): Promise<void> {
					patchState(store, { hideIncentive: true });
					try {
						await incentiveService.dismissIncentivesPanel();
					} catch {
						patchState(store, { hideIncentive: false });
					}
				},
			};
		}
	)
);
